<template>
    <div :class="['login', { 'has-error': isError }, { 'is-success': step === 4 }]">
        <!-- Ошибка -->
        <LoginErrors v-if="isShowError"
                     :title="errorTitle"
                     :text="errorText"
                     :minutes="errorTime"
                     :is-timer="errorIsTimer"
                     :is-count="errorIsCounter"
                     @clearError="clearError" />

        <AvailableError
            v-else-if="authCheckStatus === 'unavailable'"
            :error-title="errorTitle"
            :error-text="errorText"
            @checkAvailableAuth="checkAvailableAuth" />

        <div v-else
             class="login__forms">
            <transition name="fade-left-to-right"
                        mode="out-in">
                <div v-if="authCheckStatus === 'loading'"
                     key="auth-check-loader"
                     class="login__form login__form_state_loading">
                    <div class="login__form-logo">
                        <IconLogo class-name="icon-fill icon_color_blue" />
                    </div>

                    <div class="login__form-title">
                        Личный кабинет брокера
                    </div>

                    <STLoader :loading="true"
                              message="Проверка доступности"
                              modify="loader_full_size" />
                </div>

                <!-- Ввод номера и смс, 1-й шаг -->
                <FindUser
                    v-else-if="step === 1"
                    :is-sms-available="isSmsAvailable"
                    :is-call-available="isCallAvailable"
                    @goToNext="goToNext"
                    @goToBack="goToBack"
                    @showError="showError" />

                <!-- Регистрация, 2-й шаг -->
                <Registration
                    v-else-if="step === 2"
                    @goToNext="goToNext"
                    @goToBack="goToBack"
                    @showError="showError" />

                <!-- Проверка фамилии, 3-й шаг -->
                <Surname
                    v-else-if="step === 3"
                    @goToBack="goToBack"
                    @goToNext="goToNext"
                    @showError="showError" />

                <!-- Успешная регистрация / авторизация, 4-й шаг (только после
                Registration или Surname) -->
                <Success
                    v-else-if="step === 4"
                    :just-registered="prevStep === 2"
                    @goToBack="goToBack"
                    @goToNext="goToNext"
                    @showError="showError" />
            </transition>
        </div>

        <div class="login__footer">
            <a class="login__footer-phone"
               href="tel:74952800166">+7 (495) 280-01–66</a>
            <STBaseButton
                text="Сообщить о проблеме"
                icon-before="IconAlertOutlineWhite"
                icon-before-modify="icon-fill"
                modify="login__footer-action login__footer-report button_theme_reset"
                href="/report-problems" />
        </div>
    </div>
</template>

<script>
import AvailableError from './Templates/Errors/AvailabaleError';
import FindUser from './Templates/Forms/FindUser';
import LoginErrors from './Templates/Errors/Error';
import IconLogo from '@/components/Icons/Logo';
import 'moment/locale/ru';
import moment from 'moment';
import {mapGetters} from 'vuex';
import Registration from './Templates/Forms/Registration';
import Success from './Templates/Forms/Success';
import Surname from './Templates/Forms/Surname';
import Utils from '@/helpers/Utils';
import { errorTimeMessage } from '@/helpers/errorHelper';

export default {
    name: 'LoginForm',

    components: {
        AvailableError,
        FindUser,
        IconLogo,
        LoginErrors,
        Registration,
        Success,
        Surname
    },

    data() {
        return {
            // Шаг авторизации,
            // 1 - Ввод номера и смс,
            // 2 - Регистрация,
            // 3 - Проверка фамилии,
            // 4 - Уведомление об успешной регистрации/авторизации,
            step           : 1,
            prevStep       : null, // Шаг на который возвращаем пользователя по нажатию кнопки "назад"
            isError        : false, // Есть ли ошибка
            errorTitle     : '', // Заголовок ошибки
            errorText      : '', // Описание ошибки
            errorTime      : 0, // Время показа ошибки
            errorIsTimer   : false, // Показывать ли таймер у ошибки
            errorIsCounter : false, // Показываем ли кол-во оставшихся попыток
            authCheckStatus: 'loading', // Статус проверки доступности SMS/звонка
            isSmsAvailable : false, // Доступность SMS
            isCallAvailable: false, // Доступность звонка
        };
    },

    computed: {
        ...mapGetters({
            hasAttemptCount: 'GET_LOGIN_ATTEMPT_COUNT'
        }),

        isShowError() {
            return this.isError;
        }
    },

    watch: {
        hasAttemptCount(val) {
            if (val <= 0) {
                this.checkInputAttempt();
            }
        }
    },

    created() {
        this.checkBlockTerm();
        this.checkInputAttempt();
        this.checkAvailableAuth();
    },

    methods: {
        /**
         * Проверяет доступность каналов авторизации (SMS / звонок)
         */
        async checkAvailableAuth() {
            this.authCheckStatus = 'loading';

            try {
                const {isSmsAvailable, isCallAvailable, errorTitle, errorText} = await this.$store.dispatch('checkAvailableAuth');

                this.isSmsAvailable = isSmsAvailable;
                this.isCallAvailable = isCallAvailable;
                this.errorTitle = errorTitle;
                this.errorText = errorText;

                this.authCheckStatus = isSmsAvailable || isCallAvailable ?
                    'ready' :
                    'unavailable';
            } catch (error) {
                this.isSmsAvailable = false;
                this.isCallAvailable = false;
                this.errorTitle = 'Авторизация недоступна';
                this.errorText = 'Не удалось проверить способ входа. Попробуйте ещё раз.';
                this.authCheckStatus = 'unavailable';
            }
        },

        /**
         * Переходит к предыдущему шагу
         * @param step {Number} - предыдущий шаг
         */
        goToBack(step) {
            this.step = step ?
                step :
                this.prevStep ?
                    this.prevStep :
                    1;
        },

        /**
         * Переходит к следующему шагу
         * @param step {Number} - новый шаг авторизации/регистрации
         */
        goToNext(step) {
            this.prevStep = this.step;
            this.step = step;
        },

        /**
         * Показывает ошибку
         * @param code {Number} - код ошибки. 1 - ошибка обработки запроса в CRM. 2 - Блокировка на время. 3 - Ошибка со счётчиком попыток. 4 - Блокировка при привышении кол-ва попыток. 5 - Ошибка на сервере
         * @param data {Object} - данные для показа ошибки
         */
        showError({code, data = {}}) {
            switch (code) {
                case 1:
                    this.errorTitle = 'Ошибка';
                    this.errorText = 'К&nbsp;сожалению, произошла техническая ошибка. Попробуйте еще раз позже или перезагрузите страницу.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = false;
                    break;
                case 2:
                    this.errorTitle = 'Доступ заблокирован';
                    this.errorText = data?.message || 'Вы превысили лимит на количество попыток ввода кода и ваш аккаунт был заблокирован. Обратитесь к администратору для разблокировки.';
                    this.errorTime = data?.time || 0;
                    this.errorIsTimer = data?.time > 0;
                    this.errorIsCounter = false;
                    break;
                case 3:
                    this.errorTitle = 'Ошибка';
                    this.errorText = 'К&nbsp;сожалению, произошла техническая ошибка. Попробуйте еще раз или повторите попытку позже.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = true;
                    break;
                case 4:
                    this.errorTitle = 'Авторизация недоступна';
                    this.errorText = 'К&nbsp;сожалению, по&nbsp;техническим причинам в&nbsp;данный момент авторизация недоступна и&nbsp;мы&nbsp;уже работаем над решением проблемы. Попробуйте еще раз позже.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = true;
                    break;
                case 5:
                    const limitErrorCode = 429;

                    if (data?.status && data?.status === limitErrorCode) {
                        const limitErrorTime = data?.data?.seconds || 20;
                        const minutesBlock = limitErrorTime / 60;
                        const limitErrorMessage = errorTimeMessage(limitErrorTime);
                        
                        this.errorTitle = 'Доступ временно заблокирован';
                        this.errorText = limitErrorMessage;
                        this.errorTime = minutesBlock;
                        this.errorIsTimer = true;
                        this.errorIsCounter = false;

                        break;
                    }

                    this.errorTitle = 'Ошибка';
                    this.errorText = 'К сожалению, произошла техническая ошибка. Попробуйте еще раз позже или перезагрузите страницу.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = false;

                    break;
                default:
                    break;
            }

            this.isError = true;
        },

        /**
         * Проверяет есть ли время блокировки в localStorage
         */
        checkBlockTerm() {
            // Получаем все данные по блокировки из localStorage
            const blockStart = localStorage.getItem('blockStart');
            const blockTerm = localStorage.getItem('blockTerm');
            const blockText = localStorage.getItem('blockText');

            if (!blockTerm || !blockStart) {
                return;
            }

            // Текущее время
            const currentTime = moment(new Date());

            // Узнаём время окончания блокировки по формуле: время начала блокировки + продолжительность блокировки
            const blockEnd = moment(new Date(blockStart)).add(Number(blockTerm), 'minutes');

            // Если текущее время больше, чем время окончания блокировки, выходим. Иначе показываем ошибку.
            if (blockEnd.isSameOrBefore(currentTime)) {
                localStorage.removeItem('blockTerm');
                localStorage.removeItem('blockText');
                localStorage.removeItem('blockStart');
                localStorage.removeItem('blockMinutes');

                return;
            }

            // Получаем оставшееся время блокировки в минутах.
            const remainingTime = blockEnd.diff(currentTime, 'minutes', true);
            let text = blockText;

            // Формируем текст, если он не был сформирован заранее
            if (!text) {
                text = `Вы превысили лимит на количество запросов. Ваш аккаунт на время заблокирован.`;
            }

            // Показываем ошибку
            this.showError({
                code: 2,
                data: {
                    message: text,
                    time   : Number(remainingTime)
                }
            });
        },

        /**
         * Проверяет не превышено ли количество попыток запрос
         */
        checkInputAttempt() {
            if (this.hasAttemptCount > 0) {
                return;
            }

            // Время начала блокировки
            const start = sessionStorage.getItem('loginAttemptBlockStart');

            // Если нет времени начала блокировки, выходим
            if (!start) {
                return;
            }

            // Текущее время
            const currentTime = moment();
            // Продолжительность блокировки
            const term = 10;
            // Узнаём время окончания блокировки по формуле: время начала блокировки + продолжительность блокировки
            const end = moment(start).add(Number(term), 'minutes');
            const remainingMinutes = end.diff(moment(), 'minutes', true);

            // Если текущее время больше, чем время окончания блокировки, выходим. Иначе показываем ошибку.
            if (end.isSameOrBefore(currentTime)) {
                sessionStorage.removeItem('loginAttemptBlockStart');
                sessionStorage.removeItem('loginAttemptCount');

                return;
            }

            this.showError({
                code: 2,
                data: {
                    message: 'Вы превысили лимит попыток. Авторизация временно недоступна.',
                    time   : remainingMinutes
                }
            });
        },

        /**
         * Очищает ошибку
         */
        clearError() {
            this.isError = false;
        },
    }
};
</script>

<style lang="scss">
@import "Login";
</style>
